import { type ApexOptions } from 'apexcharts'
import merge from 'deepmerge'
import { array, bool, number, object, oneOfType, string } from 'prop-types'
import ChartComponent from 'react-apexcharts'

import CustomChartLegend from 'components/CustomChartLegend'
import { COLORS } from 'consts'
import { useIsMobileDevice } from 'hooks'
import { humanizeLargeNumber } from 'utils'
import { getOptionsBasedCustomLegend } from 'utils/chartOptions'

import './index.scss'

const isObject = value => value && typeof value === 'object' && !Array.isArray(value)

const amountSerieData = data => {
  const parsedData = data?.map(data =>
    isObject(data) ? Object.values(data)?.filter(value => typeof value === 'number') : data
  )
  const flatData = parsedData?.flat()
  const amountedData = flatData?.reduce((acc, curr) => acc + curr)
  return amountedData
}

const defaultOptions: ApexOptions = {
  chart: {
    id: `winnin-apexchart-${Math.random()}`,
    toolbar: {
      tools: {
        download: false,
        pan: false,
        reset: false,
        selection: false,
        zoom: false,
        zoomin: false,
        zoomout: false,
      },
      autoSelected: 'pan',
    },
    animations: {
      enabled: false,
    },
    events: {
      mounted: chart => {
        chart.windowResizeHandler()
      },
    },
    zoom: {
      enabled: false,
    },
  },
  stroke: {
    width: 2,
    curve: 'smooth',
  },
  markers: {
    size: 0,
    shape: 'circle',
  },
  xaxis: {
    labels: {
      datetimeFormatter: {
        year: 'yyyy',
        month: 'MMM/yyyy',
        day: 'dd/MM',
      },
    },
  },
  yaxis: {
    labels: {
      formatter: v => (typeof v === 'number' ? humanizeLargeNumber(v, 3) : v),
    },
  },
  colors: [COLORS.primary, COLORS.graph_item_3],
  legend: {
    show: true,
    itemMargin: {
      horizontal: 20,
    },
  },
  tooltip: {
    hideEmptySeries: false,
  },
}

const LineChart = ({
  height,
  width,
  options,
  group,
  id,
  series,
  enableCustomLegends = true,
  disableSortLegends = false,
  ...props
}: any) => {
  const isMobileDevice = useIsMobileDevice()

  const shouldEnableCustomLegends = enableCustomLegends && isMobileDevice

  const baseOptions = {
    ...options,
    chart: {
      animations: {
        enabled: false,
      },
      ...(options.chart || {}),
      group,
      id,
    },
  }

  const mergedOptions = merge(defaultOptions, baseOptions, {
    arrayMerge: (_, sourceArray) => sourceArray,
  })
  const newOptions = getOptionsBasedCustomLegend({
    options: mergedOptions,
    enableCustomLegends: shouldEnableCustomLegends,
  })

  const seriesWithAmountedData = series?.map(serie => {
    return [serie?.name, amountSerieData(serie?.data)]
  })
  const seriesMap = new Map(seriesWithAmountedData) as any

  const legends = series?.map(serie => serie?.name?.split(':')[0])?.filter(Boolean)
  const sortedLegends = disableSortLegends
    ? legends
    : [...(legends || [])]?.sort((a, b) => seriesMap.get(b) - seriesMap.get(a))

  const isLegendsEnabled = mergedOptions?.legend?.show ?? true
  const shouldShowCustomLegend = shouldEnableCustomLegends && isLegendsEnabled

  return (
    <div className="LineChart" style={{ height, width }}>
      <ChartComponent
        id={id}
        className="line-chart-container"
        type="line"
        height={height}
        width={width}
        options={newOptions}
        series={series}
        {...props}
      />
      {shouldShowCustomLegend && <CustomChartLegend chartId={id} legends={sortedLegends} />}
    </div>
  )
}

LineChart.propTypes = {
  height: oneOfType([number, string]),
  width: oneOfType([number, string]),
  options: object,
  series: array,
  enableCustomLegends: bool,
  id: string,
  disableSortLegends: bool,
  colors: object,
}

LineChart.defaultProps = {
  height: 400,
  width: 1306,
  options: {},
  enableCustomLegends: true,
}

LineChart.displayName = 'LineChart'

export default LineChart
